import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { BarChart3 } from 'lucide-react';
import type { ComputedBaseline } from '../../services/tuning';

interface BaselinePanelProps {
  baseline: ComputedBaseline | null;
  computedAt: string | null;
}

function formatPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function BaselinePanel({ baseline, computedAt }: BaselinePanelProps) {
  if (!baseline) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-text-secondary py-8 text-center">
            No baseline data computed yet. Baselines are auto-generated daily for tenants with recent incidents.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Incidents (30d)" value={baseline.incidentCount30d} />
        <Stat label="Median Risk" value={baseline.medianRiskScore} />
        <Stat label="P90 Risk" value={baseline.p90RiskScore} />
        <Stat label="Avg Feedback" value={baseline.avgFeedbackRating?.toFixed(1) ?? '---'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Alert source distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Alert Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(baseline.alertSourceDistribution)
                .sort(([, a], [, b]) => b - a)
                .map(([source, pct]) => (
                  <div key={source} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-text-secondary truncate">{source}</span>
                        <span className="text-xs font-mono text-text-primary">{formatPct(pct)}</span>
                      </div>
                      <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent-blue rounded-full"
                          style={{ width: `${Math.round(pct * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Closure classification distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Closure Classifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(baseline.closureClassificationDistribution)
                .sort(([, a], [, b]) => b - a)
                .map(([cls, pct]) => (
                  <div key={cls} className="flex items-center justify-between">
                    <span className="text-sm text-text-secondary">{cls}</span>
                    <Badge variant={cls === 'falsePositive' ? 'red' : cls === 'truePositive' ? 'green' : 'default'}>
                      {formatPct(pct)}
                    </Badge>
                  </div>
                ))}
            </div>
            <div className="mt-3 pt-3 border-t border-border-default flex items-center justify-between text-xs text-text-secondary">
              <span>Fallback rate</span>
              <span className="font-mono">{formatPct(baseline.fallbackRate / 100)}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-text-secondary mt-1">
              <span>Playbook accuracy</span>
              <span className="font-mono">{formatPct(baseline.playbookAccuracy / 100)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top noise patterns */}
      {baseline.topNoisePatterns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Top Noise Patterns
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {baseline.topNoisePatterns.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-1.5 border-b border-border-default last:border-0"
                >
                  <span className="text-sm text-text-primary truncate flex-1 mr-4">
                    {p.title}
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs font-mono text-text-secondary">{p.count}x</span>
                    {p.informationalRate > 0 && (
                      <Badge variant="yellow">{formatPct(p.informationalRate)} info</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-text-secondary text-right">
        Baseline computed {formatRelative(computedAt)}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-bg-secondary border border-border-default rounded-lg p-3">
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="text-lg font-semibold text-text-primary font-mono">{value}</div>
    </div>
  );
}
